import React, { useEffect, useState } from 'react';
import { dashboard, auth } from '../lib/api';
import { Link, useLocation } from 'react-router-dom';

const PLATFORMS = [
  {
    id: 'instagram', label: 'Instagram Professional', color: '#e1306c', emoji: '📸',
    description: 'Connect a Business or Creator account directly. A Facebook Page is not required.',
    dataUse: 'Signal requests read-only access to basic account/media information and performance insights.',
  },
  {
    id: 'facebook', label: 'Facebook Pages', color: '#1877f2', emoji: '👥',
    description: 'Connect the Facebook Pages you manage in a separate Meta authorization flow.',
    dataUse: 'Signal requests your Page list and read-only Page engagement and insights access.',
  },
  { id: 'linkedin',  label: 'LinkedIn',   color: '#0a66c2', emoji: '💼' },
  { id: 'tiktok',    label: 'TikTok',     color: '#ff0050', emoji: '🎵' },
  { id: 'x',         label: 'X / Twitter',color: '#e4e4f0', emoji: '𝕏', note: 'Requires $100/mo API plan' },
];

export default function Settings() {
  const [settings,   setSettings]   = useState(null);
  const [connections,setConnections] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [invite,     setInvite]     = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [toast,      setToast]      = useState('');
  const [connecting, setConnecting] = useState('');
  const [syncStarted,setSyncStarted] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get('connected');
    const error = params.get('error');
    const errors = {
      instagram_denied: 'Instagram access was not granted. No account was connected.',
      instagram_failed: 'Instagram could not be connected. Please try again.',
      facebook_denied: 'Facebook access was not granted. No Page was connected.',
      facebook_failed: 'Facebook Pages could not be connected. Please try again.',
      no_facebook_pages: 'No eligible Facebook Pages were returned by Meta.',
      invalid_oauth_state: 'The connection request expired. Please start again.',
    };
    if (connected) showToast(`✅ ${connected === 'instagram' ? 'Instagram' : 'Facebook'} connected successfully.`);
    if (error) showToast(`❌ ${errors[error] || 'The connection could not be completed.'}`);
  }, [location]);

  useEffect(() => {
    Promise.all([dashboard.settings(), auth.connections()])
      .then(([s, c]) => {
        setSettings(s.data);
        setConnections(Array.isArray(c.data) ? c.data : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const handleConnect = async (platformId) => {
    if (platformId !== 'instagram' && platformId !== 'facebook') {
      showToast(`${platformId} connection is not available yet.`);
      return;
    }

    setConnecting(platformId);
    try {
      const response = await auth.startOAuth(platformId);
      window.location.assign(response.data.authorizationUrl);
    } catch (error) {
      setConnecting('');
      showToast(`❌ ${error.response?.data?.error || 'Could not start the connection.'}`);
    }
  };

  const handleSync = async () => {
    try {
      await dashboard.sync();
      setSyncStarted(true);
      showToast('✅ Sync started. Recent content will appear on the Dashboard shortly.');
    } catch (error) {
      showToast('❌ Sync could not be started.');
    }
  };

  const handleDisconnect = async (platformId, accountId) => {
    if (!window.confirm(`Disconnect this account?`)) return;
    try {
      await auth.disconnect(platformId, accountId);
      setConnections(c => accountId
        ? c.filter(x => !(x.platform === platformId && x.account_id === accountId))
        : c.filter(x => x.platform !== platformId)
      );
      showToast(`Account disconnected`);
    } catch (e) {
      showToast('Failed to disconnect');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const res = await auth.invite(invite);
      setInviteLink(res.data.inviteLink);
      setInvite('');
      showToast('Invite link generated!');
    } catch (e) {
      showToast('Failed to send invite');
    }
  };

  const connMap = {};
  connections.forEach(c => { connMap[c.platform] = c; });

  if (loading) return (
    <div style={{ padding: 40, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)',
                  fontSize: 11, letterSpacing: '2px' }} className="pulsing">
      LOADING SETTINGS...
    </div>
  );

  return (
    <div style={{ padding: 32, maxWidth: 720 }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 1000,
          background: 'var(--bg-elevated)', border: '1px solid var(--accent-purple)',
          borderRadius: 8, padding: '12px 20px', fontSize: 13,
          color: 'var(--text-primary)', fontFamily: 'var(--font-mono)',
          animation: 'fadeIn 0.2s ease'
        }}>
          {toast}
        </div>
      )}

      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dim)',
                      letterSpacing: '3px', textTransform: 'uppercase', marginBottom: 6 }}>
          Workspace
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 600, color: 'var(--text-primary)' }}>
          Settings
        </h1>
      </div>

      {new URLSearchParams(location.search).get('connected') && (
        <section className="card" style={{
          marginBottom: 24, borderColor: 'rgba(95,255,200,0.45)',
          background: 'rgba(95,255,200,0.05)'
        }} aria-live="polite">
          <div style={{ color: 'var(--accent-green)', fontWeight: 600, marginBottom: 8 }}>
            Connection complete
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, marginBottom: 14 }}>
            Meta returned the account access Signal needs. Sync recent content to populate the dashboard and insights.
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="btn btn-primary" onClick={handleSync} disabled={syncStarted}>
              {syncStarted ? 'Sync started ✓' : 'Sync recent content'}
            </button>
            {syncStarted && <Link to="/" className="btn btn-ghost">Open Dashboard →</Link>}
          </div>
        </section>
      )}

      {/* Platform Connections */}
      <section style={{ marginBottom: 36 }}>
        <div className="label" style={{ marginBottom: 16 }}>Platform Connections</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {PLATFORMS.map(p => {
            const accts = connections.filter(c => c.platform === p.id);
            const hasAny = accts.length > 0;
            return (
              <div key={p.id} className="card" style={{ padding: '14px 18px' }}>
                {/* Platform header row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: hasAny ? 12 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 20 }}>{p.emoji}</span>
                    <div>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{p.label}</div>
                      {!hasAny && (
                        <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                          {p.note || 'Not connected'}
                        </div>
                      )}
                      {hasAny && (
                        <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                          {accts.length} account{accts.length > 1 ? 's' : ''} connected
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: 11, padding: '6px 16px', color: p.color, borderColor: p.color + '44' }}
                    onClick={() => handleConnect(p.id)}
                    disabled={connecting === p.id}
                    title={p.dataUse || `Connect ${p.label}`}
                    aria-label={`${hasAny ? 'Add another' : 'Connect'} ${p.label}`}
                  >
                    {connecting === p.id ? 'Opening Meta…' : hasAny ? '+ Add Account' : 'Connect'}
                  </button>
                </div>

                {p.description && (
                  <div style={{
                    marginBottom: hasAny ? 12 : 0, padding: '10px 12px', borderRadius: 6,
                    background: 'var(--bg-elevated)', color: 'var(--text-dim)', fontSize: 11,
                    lineHeight: 1.55
                  }}>
                    <div>{p.description}</div>
                    <div style={{ marginTop: 4, color: 'var(--text-primary)' }}>
                      <strong>Access used:</strong> {p.dataUse}
                    </div>
                  </div>
                )}

                {/* Individual account rows */}
                {accts.map(acct => (
                  <div key={acct.account_id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', background: 'var(--bg-elevated)',
                    borderRadius: 6, marginBottom: 6
                  }}>
                    <div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                        {acct.account_name}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                        {acct.followers > 0 && `${acct.followers.toLocaleString()} followers • `}
                        Last sync: {acct.last_synced_at
                          ? new Date(acct.last_synced_at).toLocaleDateString()
                          : 'never'}
                      </div>
                    </div>
                    <button
                      className="btn btn-danger"
                      style={{ fontSize: 10, padding: '4px 10px' }}
                      onClick={() => handleDisconnect(p.id, acct.account_id)}
                    >
                      Disconnect
                    </button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </section>

      {/* Team */}
      <section style={{ marginBottom: 36 }}>
        <div className="label" style={{ marginBottom: 16 }}>Team Members</div>
        <div className="card" style={{ marginBottom: 12 }}>
          {(settings?.members || []).map(m => (
            <div key={m.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 0', borderBottom: '1px solid var(--border)'
            }}>
              <div>
                <div style={{ fontWeight: 500 }}>{m.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{m.email}</div>
              </div>
              <span style={{
                fontSize: 10, fontFamily: 'var(--font-mono)', padding: '2px 8px',
                borderRadius: 3, background: 'rgba(139,127,255,0.1)', color: 'var(--accent-purple)',
                textTransform: 'uppercase', letterSpacing: '1px'
              }}>
                {m.role}
              </span>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="label" style={{ marginBottom: 12 }}>Invite Team Member</div>
          <form onSubmit={handleInvite} style={{ display: 'flex', gap: 10 }}>
            <input
              type="email"
              placeholder="colleague@company.com"
              value={invite}
              onChange={e => setInvite(e.target.value)}
              required
            />
            <button type="submit" className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
              Send Invite
            </button>
          </form>
          {inviteLink && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg-elevated)',
                          borderRadius: 6, fontSize: 12, fontFamily: 'var(--font-mono)',
                          color: 'var(--accent-green)', wordBreak: 'break-all' }}>
              {inviteLink}
            </div>
          )}
        </div>
      </section>

      {/* Workspace info */}
      {settings?.workspace && (
        <section>
          <div className="label" style={{ marginBottom: 16 }}>Workspace Info</div>
          <div className="card">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div className="label">Name</div>
                <div style={{ color: 'var(--text-primary)' }}>{settings.workspace.name}</div>
              </div>
              <div>
                <div className="label">Report Cadence</div>
                <div style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                  {settings.workspace.report_cadence}
                </div>
              </div>
              <div>
                <div className="label">Created</div>
                <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>
                  {new Date(settings.workspace.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
